/**
 * The branded splash — the first thing a player sees while the session
 * authenticates.
 *
 * Two responsibilities, both about how the app *starts* rather than what it
 * shows:
 *
 *  1. **Own the moment Telegram's native loader disappears.** `ready()` is
 *     deliberately not called at boot (see `initTelegram`): Telegram keeps its
 *     placeholder up until this component mounts, so the hand-off is loader →
 *     splash, never loader → blank white. The background colour is set first,
 *     matching the brand cream the mark sits on, so even the transition is
 *     seamless.
 *
 *  2. **Be cheap.** This is what loads first on a slow connection. It is a
 *     static image and a CSS animation — no data fetching, no layout work.
 */

import { useEffect } from 'react';
import { useT } from '../i18n/index.tsx';
import { notifyReady, setBackgroundColor, setHeaderColor } from '../telegram.ts';
import logoUrl from '../assets/logo.jpg';

export function Splash() {
  const t = useT();

  useEffect(() => {
    // The background the native loader is drawn against — read back from the
    // stylesheet rather than hardcoded here, so this value can never disagree
    // with what the splash actually renders.
    //
    // `--splash-bg`, not `--tg-bg`: the mark is a landscape PNG with its own
    // cream field baked in, so the splash commits to the brand colour instead
    // of following the user's theme (see the splash block in styles.css). The
    // native chrome has to match it, or the seam between Telegram's loader and
    // this frame is exactly the flash the splash exists to prevent.
    const bg = getComputedStyle(document.documentElement)
      .getPropertyValue('--splash-bg')
      .trim();
    if (bg) {
      setBackgroundColor(bg);
      setHeaderColor(bg);
    }

    // Dismiss Telegram's loader now that this frame is on screen.
    notifyReady();
  }, []);

  return (
    <div className="splash" role="status" aria-label={t.appName}>
      <img className="splash__logo" src={logoUrl} alt="" draggable={false} />
    </div>
  );
}
